import { HttpError, IHttpClient } from '../core/services/httpClient'

export type MaxReportStatus = 'IN_PROGRESS' | 'DONE' | 'ERROR'

export interface MaxReportListItem {
  reportId: string
  email: string
  tenantId: string
  conversationId: string
  conversationName?: string
  reportType: string
  reportTypeText?: string
  reportStatus: MaxReportStatus
  creationTime: string
  updatedAt?: string
  resultLocation?: string
  reportS3Key?: string
  reportBucket?: string
}

export interface MaxDownloadReportResponse {
  reportId: string
  downloadUrl: string
  expiresInSeconds: number
  bucket?: string
  key?: string
}

export type CreateMaxReportRequest = {
  conversationId: string
  reportType: string
  tenantId: string
  email: string
  messageId?: string
  conversationName?: string
}

type ReportsResponse = {
  reports?: MaxReportListItem[]
  count?: number
}

export class MaxReportService {
  constructor(private readonly httpClient: IHttpClient) {}

  async createReport(payload: CreateMaxReportRequest): Promise<void> {
    try {
      if (!payload.conversationId.trim()) {
        throw new Error('Conversation ID is required for reports')
      }
      if (!payload.reportType.trim()) {
        throw new Error('Report type is required')
      }
      if (!payload.tenantId.trim()) {
        throw new Error('Tenant ID is required for reports')
      }
      if (!payload.email.trim()) {
        throw new Error('User email is required for reports')
      }

      await this.httpClient.post('/reports', {
        tenantId: payload.tenantId,
        email: payload.email,
        conversationId: payload.conversationId,
        conversationName: payload.conversationName,
        reportType: payload.reportType,
        messageId: payload.messageId,
      })
    } catch (error) {
      throw this.handleError(error, 'Failed to submit report request')
    }
  }

  async getReports(conversationId: string, tenantId: string): Promise<MaxReportListItem[]> {
    try {
      if (!conversationId.trim()) {
        throw new Error('Conversation ID is required to fetch reports')
      }
      if (!tenantId.trim()) {
        throw new Error('Tenant ID is required to fetch reports')
      }

      const response = await this.httpClient.get<ReportsResponse>('/reports', {
        params: {
          tenantId,
          conversationId,
        },
      })

      return response.reports || []
    } catch (error) {
      throw this.handleError(error, 'Failed to fetch reports')
    }
  }

  async downloadReport(reportId: string, tenantId: string): Promise<MaxDownloadReportResponse> {
    try {
      if (!reportId.trim()) {
        throw new Error('Report ID is required')
      }
      if (!tenantId.trim()) {
        throw new Error('Tenant ID is required to download reports')
      }

      return await this.httpClient.post<MaxDownloadReportResponse>('/download-report', {
        reportId,
        tenantId,
      })
    } catch (error) {
      throw this.handleError(error, 'Failed to download report')
    }
  }

  private handleError(error: unknown, context: string): Error {
    if (error instanceof HttpError) {
      if (error.status === 401) return new Error('Unauthorized - Please log in again')
      if (error.status === 403) return new Error('Access denied - You do not have permission')
      if (error.status === 404) return new Error('Reports not found')
      if (error.status >= 500) return new Error('Server error - Please try again later')
      return new Error(`${context}: ${error.message}`)
    }

    if (error instanceof Error) {
      return error
    }

    return new Error(context)
  }
}
